/**
 * Pure alert store helpers for the Wiseguy Alerts widget.
 *
 * Identity is always external_id. Sort is board time (received_on = cluster
 * first-fill on the QF path), never WebSocket arrival order.
 */

export type AlertIdentity = {
  id: number;
  external_id?: string | null;
  received_on: string;
};

/** Dedup / map key. Prefer external_id; fall back to namespaced numeric id. */
export function alertKey(a: AlertIdentity): string {
  return a.external_id ? a.external_id : `#${a.id}`;
}

/**
 * Newest-first: received_on DESC, then external_id DESC.
 * Matches QF keyset order (when specs paging is enabled).
 */
export function compareAlertsNewestFirst(a: AlertIdentity, b: AlertIdentity): number {
  const ta = Date.parse(a.received_on);
  const tb = Date.parse(b.received_on);
  const na = Number.isFinite(ta) ? ta : 0;
  const nb = Number.isFinite(tb) ? tb : 0;
  if (nb !== na) return nb - na;

  const ida = a.external_id ?? '';
  const idb = b.external_id ?? '';
  if (idb < ida) return -1;
  if (idb > ida) return 1;
  return 0;
}

/** Sort a list newest-first (stable relative to equal keys). */
export function sortAlertsNewestFirst<T extends AlertIdentity>(alerts: T[]): T[] {
  return [...alerts].sort(compareAlertsNewestFirst);
}

/**
 * Upsert one alert into a map keyed by alertKey.
 * Returns a new Map (immutable update for React).
 */
export function upsertAlert<T extends AlertIdentity>(
  prev: ReadonlyMap<string, T>,
  alert: T
): Map<string, T> {
  const next = new Map(prev);
  next.set(alertKey(alert), alert);
  return next;
}

/**
 * Drop one alert by key. Returns `prev` unchanged when the key is absent
 * so React can skip a render.
 */
export function removeAlert<T extends AlertIdentity>(
  prev: ReadonlyMap<string, T>,
  key: string
): Map<string, T> {
  if (!prev.has(key)) return prev as Map<string, T>;
  const next = new Map(prev);
  next.delete(key);
  return next;
}

/**
 * Replace the store from a history page.
 * Live rows already in the store whose keys are in `preferLiveKeys` keep the
 * live copy (curator alert.update applied after history was requested).
 * All other history rows win; keys only present in prev and not in history are
 * dropped (session/search mode switch is a full replace of the history set).
 *
 * Live-only keys in preferLiveKeys that are not in history are retained so a
 * promotion of a row outside the current history window still shows.
 */
export function replaceFromHistory<T extends AlertIdentity>(
  prev: ReadonlyMap<string, T>,
  history: readonly T[],
  preferLiveKeys: ReadonlySet<string> = new Set()
): Map<string, T> {
  const next = new Map<string, T>();

  for (const row of history) {
    const key = alertKey(row);
    const live = prev.get(key);
    if (live && preferLiveKeys.has(key)) {
      next.set(key, live);
    } else {
      next.set(key, row);
    }
  }

  for (const key of preferLiveKeys) {
    if (next.has(key)) continue;
    const live = prev.get(key);
    if (live) next.set(key, live);
  }

  return next;
}

/** Ordered list from the map. */
export function alertsFromStore<T extends AlertIdentity>(
  store: ReadonlyMap<string, T>
): T[] {
  return sortAlertsNewestFirst([...store.values()]);
}

/**
 * ET calendar day of an ISO timestamp as YYYY-MM-DD.
 * Invalid timestamps return null.
 */
export function etDayOf(iso: string): string | null {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return null;
  return new Date(ms).toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
}

/** Today's ET calendar day YYYY-MM-DD. */
export function etToday(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
}

/**
 * ET calendar day `offsetDays` before today (offset 0 = today).
 * Calendar arithmetic via UTC midnight so DST does not skip/double days.
 */
export function etCalendarDate(offsetDays: number): string {
  const todayEt = etToday();
  const [y, m, d] = todayEt.split('-').map(Number);
  const anchor = new Date(Date.UTC(y, m - 1, d));
  anchor.setUTCDate(anchor.getUTCDate() - offsetDays);
  return anchor.toISOString().slice(0, 10);
}

/** True when the alert's board time falls on the given ET day. */
export function isOnEtDay(alert: AlertIdentity, dayYmd: string): boolean {
  return etDayOf(alert.received_on) === dayYmd;
}
